import type { ModuleId } from "../auth/config.ts";

/**
 * Small bespoke glyphs, one per module plus a few generic section markers,
 * for the dashboard layout (main.ts / setup-panel.ts).
 *
 * These are original shapes in each product's general brand color, not
 * copies of Microsoft's own logos — there is no license here to embed the
 * real ones, and a wrong or stale copy would be worse than a simple mark
 * that is honestly not trying to be the official icon.
 */

const MODULE_ICON: Record<ModuleId, { bg: string; glyph: string }> = {
    teams: {
        bg: "#5b5fc7",
        glyph: `<circle cx="9" cy="9" r="4" fill="#fff"/><circle cx="15.5" cy="10.5" r="3" fill="#fff" fill-opacity=".75"/>`
    },
    mail: {
        bg: "#0078d4",
        glyph: `<rect x="4" y="6" width="16" height="12" rx="1.5" fill="#fff"/><path d="M4.5 7 12 13 19.5 7" stroke="#0078d4" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
    },
    onedrive: {
        bg: "#1490df",
        glyph: `<path d="M7 15a3.5 3.5 0 0 1 .4-6.98 4.5 4.5 0 0 1 8.5-1.2A3.75 3.75 0 0 1 17.5 15H7z" fill="#fff"/>`
    },
    calendar: {
        bg: "#0f6cbd",
        glyph: `<rect x="4.5" y="5.5" width="15" height="13" rx="1.5" fill="#fff"/><rect x="4.5" y="5.5" width="15" height="3.5" rx="1.5" fill="#083a68"/><rect x="7" y="12" width="2.4" height="2.4" fill="#0f6cbd"/><rect x="11" y="12" width="2.4" height="2.4" fill="#0f6cbd"/><rect x="15" y="12" width="2.4" height="2.4" fill="#0f6cbd"/>`
    },
    contacts: {
        bg: "#038387",
        glyph: `<circle cx="12" cy="9.5" r="3.2" fill="#fff"/><path d="M5.5 18c1-3.2 3.7-4.8 6.5-4.8s5.5 1.6 6.5 4.8" fill="#fff"/>`
    },
    tasks: {
        bg: "#3059a5",
        glyph: `<rect x="5" y="5" width="14" height="14" rx="2.5" fill="none" stroke="#fff" stroke-width="1.6"/><path d="M8.3 12.2 11 14.8l4.7-5.4" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
    },
    onenote: {
        bg: "#7719aa",
        glyph: `<rect x="5" y="4.5" width="12" height="15" rx="1.2" fill="#fff"/><rect x="17" y="8" width="2.4" height="2.6" fill="#fff" fill-opacity=".8"/><rect x="17" y="13" width="2.4" height="2.6" fill="#fff" fill-opacity=".8"/><rect x="8" y="8" width="6" height="1.4" fill="#7719aa"/><rect x="8" y="11" width="6" height="1.4" fill="#7719aa"/><rect x="8" y="14" width="4" height="1.4" fill="#7719aa"/>`
    }
};

function svg(inner: string): string {
    return `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">${inner}</svg>`;
}

/** A 24×24 rounded-square badge for one module, in its own brand-ish color. */
export function moduleIconSvg(id: ModuleId): string {
    const { bg, glyph } = MODULE_ICON[id];
    return svg(`<rect width="24" height="24" rx="6" fill="${bg}"/>${glyph}`);
}

/** Generic section markers — plain line glyphs, no brand color, for the section headers. */
export const SECTION_ICON = {
    auth: svg(
        `<circle cx="12" cy="8.5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M5.8 18.5c1.1-3.3 3.8-5 6.2-5s5.1 1.7 6.2 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`
    ),
    setup: svg(
        `<rect x="4.5" y="5" width="15" height="4.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="4.5" y="10.7" width="15" height="4.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="4.5" y="16.4" width="9" height="4.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/>`
    ),
    export: svg(
        `<path d="M4.5 8V6.5A1.5 1.5 0 0 1 6 5h4l1.6 2H18a1.5 1.5 0 0 1 1.5 1.5V17A1.5 1.5 0 0 1 18 18.5H6A1.5 1.5 0 0 1 4.5 17z" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M12 11v4.2M9.8 13.2 12 15.4l2.2-2.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
    )
};

export function iconElement(markup: string, className: string): HTMLSpanElement {
    const span = document.createElement("span");
    span.className = className;
    span.innerHTML = markup;
    return span;
}
